<script lang="ts">
  // (c) Cognisoft Aps, 2022, for Svelte 5: 2026

  // prompted naming, self-evaluation
  // naming of object, verb-object or subject-verb-object (extension)
  // - suggest repetition if not satisfied with own naming

  import { onDestroy } from "svelte";
  import { crossfade } from "svelte/transition";
  import { flip } from "svelte/animate";

  import {
    Pages,
    DaDK,
    Path,
    Extension,
    Level,
    LevelExtension,
    Gender,
  } from "../enums";
  import { firstWaitSecs } from "../constants";
  import { targetVerbAndObjects } from "../globals/targets"; // last picture shown in step 8

  import { route } from "../stores/route.svelte";
  import { studentUserData } from "../stores/user-store.svelte";
  import { progress } from "../stores/progress.svelte";
  import { helpCount } from "../stores/helpCount.svelte";
  import { result } from "../stores/result.svelte";
  import { tasksCompleted } from "../stores/tasksCompleted.svelte";
  import { levelExtensionProgress } from "../stores/level-extension-progress.svelte";

  import { Icons } from "../utilities/icons";
  import { playSoundWithAudioTag } from "../utilities/playSound";
  import { getTotalNumberOfTasks } from "../utilities/getTotalNumberOfTasks";
  import { setCompletedBackgroundColor } from "../utilities/setCompletedBackgroundColor";
  import { logger } from "../utilities/logger";

  import ExitModal from "../modals/ExitModal.svelte";

  import IconButton from "../components/IconButton.svelte";
  import RoundButton from "../components/RoundButton.svelte";
  import ProgressBar from "../components/ProgressBar.svelte";
  import PauseMessage from "../components/PauseMessage.svelte";

  // PROPS --------------------------------------------------

  // one audiotag in App.svelte is used on all pages in order to have
  //   sound played even before mouse click

  let { audioTag }: { audioTag: HTMLAudioElement } = $props();

  // TYPES --------------------------------------------------

  // currentState during working with task,
  //   tells whether image buttons should be disabled etc.
  const State = {
    WaitToStart: "waitToStart",
    PlayingInstruction: "playingInstruction",
    WaitToPromptInitial: "waitToPromptInitial",
    PlayingPromptInitial: "playingPromptInitial",
    WaitingForAnswerInitial: "waitingForAnswerInitial",
    PlayingPromptRepeat: "playingPromptRepeat",
    WaitingForAnswerAfterAnswerNo: "waitingForAnswerAfterAnswerNo",
    WaitForAnswerAfterTryAgain: "waitForAnswerAfterTryAgain",
    GotNoResponse: "gotNoResponse",
    WaitAfterYesOrFinished: "waitAfterYesOrFinished",
    Paused: "paused",
    ExitModalShown: "exitModalShown",
  };

  type State = (typeof State)[keyof typeof State];

  type imgAndVerb = {
    img: string;
    verb: string;
  };

  type HelpLevel = 1 | 2 | 3 | 4 | 5 | 6;

  // CONSTANTS --------------------------------------------

  // needed for Svelte animation
  const [send, receive] = crossfade({});

  const waitAfterAnswerDelay = 2000;
  const subjectNoResponseDelay = 2000;
  const waitAfterNoResponseDelay = 5000;

  // STATE ------------------------------------------------

  let currentState: State = $state(State.WaitToStart);
  let oldState: State = $state(State.WaitToStart); // currentState to return to after pause or exit dialog

  let newTask = $state(true); // in order to not play recording from previous picture/task

  let stage: 0 | 1 | 2 | 3 = $state(0); // 0-3, one stage for each sentence presented
  let sendImgs: Array<imgAndVerb> = $state([]); // animation: items at bottom that move up one at a time

  let receiveImgs: Array<imgAndVerb> = $state([]); // an array in order to use animation

  let targetShowing = $state(false); // object is hidden, except in repeat phase

  let helpLevel = $state(1);

  let repeatButtonTimer: ReturnType<typeof setTimeout>;
  let helpTimer: ReturnType<typeof setTimeout>;

  let disableAllButtons = $state(true); // mostly when playing sounds
  let allowRepetition = $state(false);
  let requestedHelpPaused = $state(true); // help not active for some seconds after activated last time
  let enhanceEvaluateArea = $state(false); //

  // for results:
  let gotHelpForItem = $state(false);
  let hasErrorForItem = $state(false);

  // FOR RESEARCH ONLY:
  // // save results for each response, push to array in results.store
  // let presentTime: Date;
  // let numberOfTimedHelp = 0;
  // let numberOfRequestedHelp = 0;
  // let numberOfRequestedRepetitions = 0;
  // let attempt = 1;
  // let responseDetails: Array<ResponseDetail> = [];

  // underline for hidden word - length computed
  let underlineString = $state("");

  let waitToGotoNext = $state(false); // OBS - should be a STATE ??? ************ !!!

  // keep track of pause time
  let pauseStartTime: number;
  let pauseEndTime: number;

  // EFFECTS --------------------------------------------------

  $effect(() => {
    logger("Step9 currentState:", currentState);
  });
  $effect(() => {
    logger("Step9 stage:", stage);
  });
  $effect(() => {
    logger("Step9 disableAllButtons:", disableAllButtons);
  });
  $effect(() => {
    logger("Step9 helpLevel:", helpLevel);
  });

  // FUNCTIONS --------------------------------------------

  const countHelp = () => {
    switch (stage) {
      case 0:
        helpCount.value.naming.item91++;
        break;
      case 1:
        helpCount.value.naming.item92++;
        break;
      case 2:
        helpCount.value.naming.item93++;
        break;
      case 3:
        helpCount.value.naming.item94++;
        break;
    }
  };

  // show repeat button a defined time after showin task

  const startRepeatButtonTimer = () => {
    allowRepetition = false;
    repeatButtonTimer = setTimeout(() => {
      allowRepetition = true;
    }, studentUserData.value.settings.repeatDelaySecs * 1000);
  };

  // showing help according to 2 level hierarkies (timed and requested help) ---

  const startHelpTimer = () => {
    disableAllButtons = false;
    helpTimer = setTimeout(() => {
      // numberOfTimedHelp++; // FOR RESEARCH ONLY:
      giveNoResponseTimedHelp();
    }, studentUserData.value.settings.helpDelaySecs * 1000);
  };

  const enableHelpButton = () => {
    disableAllButtons = false;
    requestedHelpPaused = false;
  };

  const afterHelp = () => {
    startHelpTimer();
    enableHelpButton();
  };

  const NoResponseForLevelExtionsionProgressObject1 = () => {
    // if (
    //     currentState === State.Paused ||
    //     currentState === State.ExitModalShown ||
    //     currentState === State.WaitAfterYesOrFinished
    //   ) {
    //     return;
    //   }
    playSoundWithAudioTag(
      audioTag,
      DaDK.SaySentenceTogether,
      NoResponseForLevelExtionsionProgressObject2,
    );
  };

  const NoResponseForLevelExtionsionProgressObject2 = () => {
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[stage].object,
      NoResponseForLevelExtionsionProgressObject3,
    );
  };

  const NoResponseForLevelExtionsionProgressObject3 = () => {
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[stage].sentence,
      waitAfterNoResponse,
    );
  };

  const NoResponseForLevelExtionsionProgressVerb1 = () => {
    playSoundWithAudioTag(
      audioTag,
      DaDK.SaySentenceTogether,
      NoResponseForLevelExtionsionProgressVerb2,
    );
  };

  const NoResponseForLevelExtionsionProgressVerb2 = () => {
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[stage].sentence,
      waitAfterNoResponse,
    );
  };

  const NoResponseForLevelExtionsionProgressSubject1 = () => {
    playSoundWithAudioTag(
      audioTag,
      DaDK.SaySentenceTogether,
      NoResponseForLevelExtionsionProgressSubject2,
    );
  };

  const NoResponseForLevelExtionsionProgressSubject2 = () => {
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[stage].sentenceFull,
      NoResponseForLevelExtionsionProgressSubject3,
    );
  };

  const NoResponseForLevelExtionsionProgressSubject3 = () => {
    setTimeout(() => {
      NoResponseForLevelExtionsionProgressSubject4();
    }, subjectNoResponseDelay);
  };

  const NoResponseForLevelExtionsionProgressSubject4 = () => {
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[stage].sentenceFull,
      waitAfterNoResponse,
    );
  };

  const heSheHelpPrompt = () => {
    if (targetVerbAndObjects.value[stage].subjectGender === Gender.Male) {
      playSoundWithAudioTag(audioTag, DaDK.He_, afterHelp);
    } else {
      playSoundWithAudioTag(audioTag, DaDK.She_, afterHelp);
    }
  };

  const waitAfterNoResponse = () => {
    setTimeout(() => {
      nextItemOrLeavePage();
    }, waitAfterNoResponseDelay);
  };

  const giveNoResponseTimedHelp = () => {
    logger("Step9 giveNoResponseTimedHelp");
    disableAllButtons = true;
    countHelp();
    clearTimeout(helpTimer);
    requestedHelpPaused = true;
    gotHelpForItem = true;
    switch (helpLevel) {
      case 1:
        helpLevel = 2;
        switch (levelExtensionProgress.value) {
          case LevelExtension.Object:
            playSoundWithAudioTag(
              audioTag,
              targetVerbAndObjects.value[stage].namingShortPrompt,
              afterHelp,
            );
            break;
          case LevelExtension.Verb:
            playSoundWithAudioTag(
              audioTag,
              targetVerbAndObjects.value[stage].namingFullSentencePrompt,
              heSheHelpPrompt,
            );
            break;
          case LevelExtension.Subject:
            playSoundWithAudioTag(
              audioTag,
              targetVerbAndObjects.value[stage].namingFullSentencePrompt,
              afterHelp,
            );
            break;
        }
        break;
      case 2:
        helpLevel = 3;
        switch (levelExtensionProgress.value) {
          case LevelExtension.Object:
            playSoundWithAudioTag(
              audioTag,
              targetVerbAndObjects.value[stage].namingShortPrompt,
              afterHelp,
            );
            break;
          case LevelExtension.Verb:
            playSoundWithAudioTag(
              audioTag,
              targetVerbAndObjects.value[stage].namingFullSentencePrompt,
              heSheHelpPrompt,
            );
            break;
          case LevelExtension.Subject:
            playSoundWithAudioTag(
              audioTag,
              targetVerbAndObjects.value[stage].subjectName,
              afterHelp,
            );
            break;
        }
        break;
      case 3:
        helpLevel = 4;
        switch (levelExtensionProgress.value) {
          case LevelExtension.Object:
            playSoundWithAudioTag(
              audioTag,
              targetVerbAndObjects.value[stage].object,
              afterHelp,
            );
            break;
          case LevelExtension.Verb:
            heSheHelpPrompt();
            break;
          case LevelExtension.Subject:
            playSoundWithAudioTag(
              audioTag,
              targetVerbAndObjects.value[stage].sentenceFull,
              afterHelp,
            );
            break;
        }
        break;
      case 4:
        helpLevel = 5;
        switch (levelExtensionProgress.value) {
          case LevelExtension.Object:
            playSoundWithAudioTag(
              audioTag,
              targetVerbAndObjects.value[stage].object,
              afterHelp,
            );
            break;
          case LevelExtension.Verb:
            heSheHelpPrompt();
            break;
          case LevelExtension.Subject:
            playSoundWithAudioTag(
              audioTag,
              targetVerbAndObjects.value[stage].sentenceFull,
              afterHelp,
            );
            break;
        }
        break;
      case 5:
        helpLevel = 6;
        enhanceEvaluateArea = true;
        playSoundWithAudioTag(audioTag, DaDK.AreYouSatisfied, afterHelp);
        break;
      case 6:
        currentState = State.GotNoResponse;
        switch (levelExtensionProgress.value) {
          case LevelExtension.Object:
            NoResponseForLevelExtionsionProgressObject1();
            break;
          case LevelExtension.Verb:
            NoResponseForLevelExtionsionProgressVerb1();
            break;
          case LevelExtension.Subject:
            NoResponseForLevelExtionsionProgressSubject1();
            break;
        }
        break;
      default:
        console.error(
          "error in giveNoResponseTimedHelp() switch, helpLevel: ",
          helpLevel,
        );
    }
  };

  const afterRequestedHelp = () => {
    startHelpTimer();
    disableAllButtons = false;
    requestedHelpPaused = false;
  };

  const requestedHelp = () => {
    logger("Step9 requestedHelp");
    countHelp();
    disableAllButtons = true;
    // numberOfRequestedHelp++;  // FOR RESEARCH ONLY
    clearTimeout(helpTimer);
    requestedHelpPaused = true;
    if (levelExtensionProgress.value === LevelExtension.Subject) {
      playSoundWithAudioTag(
        audioTag,
        DaDK.Step9HelpSubject,
        afterRequestedHelp,
      );
    } else {
      playSoundWithAudioTag(audioTag, DaDK.Step9Help, afterRequestedHelp);
    }
  };

  // pause before sentence is played
  const waitAndStart = (): void => {
    setTimeout(() => {
      playIntro();
    }, firstWaitSecs);
  };

  const playIntro = (): void => {
    if (levelExtensionProgress.value === LevelExtension.Object) {
      playSoundWithAudioTag(audioTag, DaDK.Step9IntroObject, waitAfterIntro);
    } else if (levelExtensionProgress.value === LevelExtension.Verb) {
      playSoundWithAudioTag(audioTag, DaDK.Step9IntroVerb, waitAfterIntro);
    } else {
      playSoundWithAudioTag(audioTag, DaDK.Step9IntroSubject, waitAfterIntro);
    }
  };

  const waitAfterIntro = (): void => {
    setTimeout(() => {
      presentNextTask();
    }, firstWaitSecs);
  };

  const repeatSentence = () => {
    // console.error("repeatSentence()");
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[stage].sentence,
      startHelpTimer,
    );
  };

  const repeatSentenceFull = () => {
    // console.error("repeatSentence()");
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[stage].sentenceFull,
      startHelpTimer,
    );
  };

  // repeat sentence (after speaker button press)
  const repeatSound = (): void => {
    logger("Step9 repeatSound clicked");
    disableAllButtons = true;
    countHelp();
    // numberOfRequestedRepetitions++;  // FOR RESEARCH ONLY
    clearTimeout(helpTimer);
    switch (levelExtensionProgress.value) {
      case LevelExtension.Object:
        playSoundWithAudioTag(
          audioTag,
          targetVerbAndObjects.value[stage].object,
          repeatSentence,
        );
        break;
      case LevelExtension.Verb:
        playSoundWithAudioTag(
          audioTag,
          targetVerbAndObjects.value[stage].verb,
          repeatSentence,
        );
        break;
      case LevelExtension.Subject:
        repeatSentenceFull();
        break;
    }
  };

  const playPrompt = () => {
    let taskSentence: string;
    if (levelExtensionProgress.value === LevelExtension.Subject) {
      taskSentence = targetVerbAndObjects.value[stage].namingFullSentencePrompt;
    } else {
      if (studentUserData.value.settings.perseveration) {
        if (levelExtensionProgress.value === LevelExtension.Verb) {
          taskSentence = targetVerbAndObjects.value[stage].namingVerbIntro;
        } else {
          taskSentence = targetVerbAndObjects.value[stage].namingIntro;
        }
      } else {
        if (levelExtensionProgress.value === LevelExtension.Verb) {
          taskSentence =
            targetVerbAndObjects.value[stage].namingVerbSuggestionFalse;
        } else {
          taskSentence =
            targetVerbAndObjects.value[stage].namingSuggestionFalse;
        }
      }
    }
    currentState = State.PlayingPromptInitial;
    playSoundWithAudioTag(audioTag, taskSentence, readyForResponse);
  };

  const readyForResponse = () => {
    // console.error("readyForResponse");
    currentState = State.WaitingForAnswerInitial;
    enableHelpButton();
    startHelpTimer();
    startRepeatButtonTimer();
  };

  // progress in task logic --

  const presentNextTask = () => {
    currentState = State.WaitToStart;
    // initializeResultVariables();  // FOR RESEARCH ONLY:
    enhanceEvaluateArea = false;
    // attempt = 1;  // FOR RESEARCH ONLY
    helpLevel = 1;
    newTask = true;
    if (stage > 0) {
      targetShowing = false;
      // move target back down
      sendImgs.splice(stage - 1, 0, receiveImgs[0]);
      sendImgs = sendImgs;
      receiveImgs = [];
      setTimeout(() => {
        presentTask();
      }, firstWaitSecs);
    } else {
      presentTask();
    }
  };

  const presentTask = () => {
    setTimeout(() => {
      receiveImgs = [];
      receiveImgs.push(sendImgs[stage]);
      receiveImgs = receiveImgs;
      sendImgs.splice(stage, 1);
      sendImgs = sendImgs;
      targetShowing = true;
      playPrompt();
    }, firstWaitSecs);
  };

  // step 8 and 9 repeated for level 2.1 and 2.2 with
  //   verb or subject extension
  const gotoExtensionStep8 = () => {
    route.current = Pages.Step8;
  };

  // no repetition of step 8 and nine for level 1 and 2
  //  without verb or subject extension
  const gotoResults = () => {
    route.current = Pages.EndResults;
  };

  const nextItemOrLeavePage = () => {
    tasksCompleted.value = tasksCompleted.value + 1;
    allowRepetition = false;
    requestedHelpPaused = true;
    gotHelpForItem = false;
    hasErrorForItem = false;
    clearTimeout(helpTimer);
    clearTimeout(repeatButtonTimer);
    if (stage < 3) {
      stage++;
      presentNextTask();
    } else {
      targetShowing = false;
      waitToGotoNext = true;
      setCompletedBackgroundColor(true);
      // result.value.responseDetails.push(...responseDetails);
      result.value.lastStepCompleted = 9;
      // end and go to results if:
      // first or second level -
      // or if intro or basis-test -
      // or if ended verb or subject part
      if (
        studentUserData.value.currentLevel === Level.First ||
        studentUserData.value.currentLevel === Level.Second ||
        studentUserData.value.settings.doIntroNextSession ||
        studentUserData.value.settings.doTestNextSession ||
        (studentUserData.value.currentLevel === Level.SecondVerb &&
          levelExtensionProgress.value === LevelExtension.Verb) ||
        (studentUserData.value.currentLevel === Level.SecondSubject &&
          levelExtensionProgress.value === LevelExtension.Subject)
      ) {
        setTimeout(() => {
          gotoResults();
        }, waitAfterAnswerDelay);
        return;
      } else {
        // update progress for level 8 and 9
        if (levelExtensionProgress.value === LevelExtension.Object) {
          levelExtensionProgress.value = LevelExtension.Verb;
        } else if (levelExtensionProgress.value === LevelExtension.Verb) {
          levelExtensionProgress.value = LevelExtension.Subject;
        }
        // determine if step 8 or 9 should be repeated
        if (levelExtensionProgress.value === LevelExtension.Verb) {
          // playSoundWithAudioTag(
          //   audioTag,
          //   "nu skal vi tale om hvad personerne gør",
          //   gotoExtensionStep8,
          // );
          gotoExtensionStep8();
        } else if (levelExtensionProgress.value === LevelExtension.Subject) {
          // playSoundWithAudioTag(
          //   audioTag,
          //   "nu får personerne navne",
          //   gotoExtensionStep8,
          // );
          gotoExtensionStep8();
        } else {
          console.error(
            "error step 9, levelExtensionProgress: ",
            levelExtensionProgress.value,
          );
        }
      }
    }
  };

  // self evaluation of answer ---

  const acceptsOwnAnswer1 = () => {
    logger("Step9; acceptsOwnAnswer1 clicked");
    disableAllButtons = true;
    clearTimeout(helpTimer);
    currentState = State.WaitAfterYesOrFinished;
    saveResultVariablesWithCorrect(true);
    if (!gotHelpForItem && !hasErrorForItem) {
      result.value.namingCorrectFirstTry =
        result.value.namingCorrectFirstTry + 1;
    }
    progress.value += 1;
    progress;
    switch (levelExtensionProgress.value) {
      case LevelExtension.Object:
        playSoundWithAudioTag(
          audioTag,
          targetVerbAndObjects.value[stage].object,
          acceptsOwnAnswer2,
        );
        break;
      case LevelExtension.Verb:
        playSoundWithAudioTag(
          audioTag,
          targetVerbAndObjects.value[stage].sentenceOK,
          nextItemOrLeavePage,
        );
        break;
      case LevelExtension.Subject:
        playSoundWithAudioTag(
          audioTag,
          targetVerbAndObjects.value[stage].sentenceFull,
          nextItemOrLeavePage,
        );
        break;
    }
  };

  const acceptsOwnAnswer2 = () => {
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[stage].sentenceOK,
      nextItemOrLeavePage,
    );
  };

  const rejectsOwnAnswer = () => {
    logger("Step9; rejectsOwnAnswer clicked");
    clearTimeout(helpTimer);
    countHelp();
    saveResultVariablesWithCorrect(false);
    // attempt++; // FOR RESEARCH ONLY:
    // initializeResultVariables();  // FOR RESEARCH ONLY:
    currentState = State.WaitingForAnswerAfterAnswerNo;
    hasErrorForItem = true;
    repeatWithMe();
  };

  const givingUp = () => {
    logger("Step9; givingUp clicked");
    currentState = State.WaitAfterYesOrFinished;
    disableAllButtons = true;
    clearTimeout(helpTimer);
    saveResultVariablesWithCorrect(false);
    hasErrorForItem = true;
    nextItemOrLeavePage();
  };

  // if answer is rejected then show repeat-with-me section

  const repeatWithMe = () => {
    disableAllButtons = true;
    if (levelExtensionProgress.value === LevelExtension.Object) {
      playSoundWithAudioTag(audioTag, DaDK.SayWordTogether, repeatWithMe2);
    } else {
      playSoundWithAudioTag(audioTag, DaDK.SaySentenceTogether, repeatWithMe2);
    }
  };

  const repeatWithMe2 = () => {
    if (levelExtensionProgress.value === LevelExtension.Object) {
      playSoundWithAudioTag(
        audioTag,
        targetVerbAndObjects.value[stage].object,
        delayBeforeRepeatWithMe3,
      );
    } else if (levelExtensionProgress.value === LevelExtension.Verb) {
      playSoundWithAudioTag(
        audioTag,
        targetVerbAndObjects.value[stage].verb,
        delayBeforeRepeatWithMe3,
      );
    } else {
      playSoundWithAudioTag(
        audioTag,
        targetVerbAndObjects.value[stage].sentenceFull,
        delayBeforeRepeatWithMe3,
      );
    }
  };

  const delayBeforeRepeatWithMe3 = () => {
    setTimeout(() => {
      repeatWithMe3();
    }, waitAfterAnswerDelay);
  };

  const repeatWithMe3 = () => {
    if (
      levelExtensionProgress.value === LevelExtension.Object ||
      levelExtensionProgress.value === LevelExtension.Verb
    ) {
      playSoundWithAudioTag(
        audioTag,
        targetVerbAndObjects.value[stage].sentence,
        repeatWithMe4,
      );
    } else {
      playSoundWithAudioTag(
        audioTag,
        targetVerbAndObjects.value[stage].sentenceFull,
        repeatWithMe4,
      );
    }
  };

  const repeatWithMe4 = () => {
    // console.error("repeatWithMe4()");
    helpLevel = 6;
    startHelpTimer();
    currentState = State.WaitForAnswerAfterTryAgain;
    disableAllButtons = false;
  };

  // pause --------

  const pauseClicked = (): void => {
    if (currentState === State.Paused) {
      pauseEndTime = Date.now();
      result.value.pauseElapsed =
        result.value.pauseElapsed + Math.abs(pauseEndTime - pauseStartTime);
      currentState = oldState;
      allowRepetition = true;
      startHelpTimer();
    } else {
      pauseStartTime = Date.now();
      clearTimeout(repeatButtonTimer);
      clearTimeout(helpTimer);
      oldState = currentState;
      currentState = State.Paused;
    }
  };

  // exit task prematurely after stop button clicked

  // stop button: show yes-no modal
  const exitButtonClicked = () => {
    clearTimeout(repeatButtonTimer);
    clearTimeout(helpTimer);
    oldState = currentState;
    currentState = State.ExitModalShown;
  };

  // exit task (modal event "yes")
  const exitTask = () => {
    // console.error("exitTask()");
    clearTimeout(repeatButtonTimer);
    clearTimeout(helpTimer);
    result.value.wasInterrupted = true;
    route.current = Pages.EndResults;
  };

  // dismiss exit modal (event "no")
  const dismissExitModal = () => {
    // console.error("dismissExitModal()");
    currentState = oldState;
    allowRepetition = true;
    startHelpTimer();
  };

  // FOR RESEARCH ONLY: --------------------------------------------

  // save result details for a single item, used for cvs results
  // *** used for research disabled in current production
  const saveResultVariablesWithCorrect = (correct: boolean) => {
    // // for research - disabled in current production
    // responseDetails.push({
    //   step: 9,
    //   attempt: attempt,
    //   presentTime: presentTime,
    //   responseTime: new Date(),
    //   targetItem: targetVerbAndObjects.value[stage].sentence,
    //   correct: correct,
    //   numberOfTimedHelp: numberOfTimedHelp,
    //   numberOfRequestedHelp: numberOfRequestedHelp,
    //   numberOfRequestedRepetitions: numberOfRequestedRepetitions,
    // });
  };

  // LIFE CYCLE ---------------------------------------------------

  onDestroy(() => {
    clearTimeout(helpTimer);
  });

  // INITIALIZE --------------------------------------------

  // FOR RESEARCH ONLY:
  //  const initializeResultVariables = () => {
  //   presentTime = new Date();
  //   numberOfTimedHelp = 0;
  //   numberOfRequestedHelp = 0;
  //   numberOfRequestedRepetitions = 0;
  // };

  setCompletedBackgroundColor(false);

  if (levelExtensionProgress.value === LevelExtension.Object) {
    underlineString = "_".repeat(
      targetVerbAndObjects.value[stage].object.length,
    );
  } else if (levelExtensionProgress.value === LevelExtension.Verb) {
    underlineString = "_".repeat(targetVerbAndObjects.value[stage].verb.length);
  } else {
    underlineString =
      "_".repeat(targetVerbAndObjects.value[stage].verb.length) +
      " " +
      "_".repeat(targetVerbAndObjects.value[stage].object.length);
  }

  // for items to chose from at bottom:
  targetVerbAndObjects.value.forEach((element) => {
    sendImgs.push({ img: element.image, verb: element.verb });
  });

  // wait before presenting target sentence
  waitAndStart();
</script>

<!-- HTML ===================================================== -->

<main data-page="step9">
  <div
    data-testid="state"
    data-state={currentState}
    style="display: none;"
  ></div>
  <!-- top panel with help and repeat buttons -->
  <div class="top-panel">
    <div>
      <span class="stop-button">
        <RoundButton
          disabled={currentState === State.WaitToStart ||
            currentState === State.PlayingInstruction ||
            currentState === State.WaitToPromptInitial ||
            currentState == State.GotNoResponse ||
            currentState === State.WaitAfterYesOrFinished ||
            currentState === State.PlayingPromptRepeat ||
            currentState === State.Paused ||
            disableAllButtons}
          onclick={exitButtonClicked}
          icon={Icons.TrafficLightRed}
        />
      </span>
      <RoundButton
        disabled={currentState === State.WaitToStart ||
          currentState === State.PlayingInstruction ||
          currentState === State.WaitToPromptInitial ||
          currentState == State.GotNoResponse ||
          currentState === State.WaitAfterYesOrFinished ||
          currentState === State.PlayingPromptRepeat ||
          disableAllButtons}
        onclick={pauseClicked}
        icon={currentState === State.Paused ? Icons.Play : Icons.Pause}
      />
    </div>
    <div>
      <RoundButton
        disabled={currentState === State.WaitToStart ||
          currentState === State.PlayingInstruction ||
          currentState === State.WaitToPromptInitial ||
          currentState == State.GotNoResponse ||
          currentState === State.WaitAfterYesOrFinished ||
          currentState === State.PlayingPromptRepeat ||
          currentState === State.Paused ||
          !allowRepetition ||
          requestedHelpPaused ||
          disableAllButtons}
        onclick={repeatSound}
        icon={Icons.VolumeRegular}
      />
      <span class="help-button">
        <RoundButton
          disabled={currentState === State.WaitToStart ||
            currentState === State.PlayingInstruction ||
            currentState === State.WaitToPromptInitial ||
            currentState == State.GotNoResponse ||
            currentState === State.WaitAfterYesOrFinished ||
            currentState === State.PlayingPromptRepeat ||
            currentState === State.Paused ||
            requestedHelpPaused ||
            disableAllButtons}
          onclick={requestedHelp}
          icon={Icons.QuestionSolid}
        />
      </span>
    </div>
  </div>

  <!-- middle area to show target picture and buttons for recording etc -->
  <!-- target image to left -->
  <div class="target-area">
    <div class="target">
      <!-- image showing target -->
      {#each receiveImgs as item (item)}
        <div
          class="image"
          animate:flip
          out:send|global={{ key: item }}
          in:receive|global={{ key: item }}
        >
          <picture>
            <source
              type="image/webp"
              srcset={encodeURI(Path.Img + item.img + Extension.Webp)}
              draggable={false}
            />
            <img
              class="target-img"
              style="background-image: url('{Path.ImgBg}{item.img}{Extension.ImgBg}'); 
                background-image: image-set('{Path.ImgBg}{item.img}{Extension.WebpBg}'); 
                background-size: cover;"
              draggable={false}
              src={encodeURI(Path.Img + item.img + Extension.Png)}
              alt=""
            />
          </picture>
        </div>
      {/each}
    </div>
  </div>

  <div class="text">
    <!-- showing verb or subject, word hidden if not repeat task after error -->
    <div class="button-text" class:hidden={!targetShowing}>
      <div
        class="text-start"
        class:short-text-start={levelExtensionProgress.value ===
          LevelExtension.Subject}
      >
        {#if levelExtensionProgress.value === LevelExtension.Object}
          {targetVerbAndObjects.value[stage].verb}
        {:else if levelExtensionProgress.value === LevelExtension.Verb}
          {#if currentState === State.WaitToPromptInitial || currentState === State.PlayingPromptInitial || currentState === State.WaitingForAnswerInitial || currentState === State.Paused || currentState === State.ExitModalShown}
            {underlineString}
          {:else}
            {targetVerbAndObjects.value[stage].verb}
          {/if}
        {:else}
          <span class="name-caps">
            {targetVerbAndObjects.value[stage].subjectName}
          </span>
        {/if}
      </div>
      <!-- showing object or verb + object, word hidden for object tasks if not repeat after error -->
      <div
        class="text-end"
        class:long-text-end={levelExtensionProgress.value ===
          LevelExtension.Subject}
      >
        {#if levelExtensionProgress.value === LevelExtension.Verb}
          {targetVerbAndObjects.value[stage].object}
        {:else if currentState === State.WaitToPromptInitial || currentState === State.PlayingPromptInitial || currentState === State.WaitingForAnswerInitial || currentState === State.Paused || currentState === State.ExitModalShown}
          {underlineString}
        {:else if levelExtensionProgress.value === LevelExtension.Object}
          {targetVerbAndObjects.value[stage].object}
        {:else if levelExtensionProgress.value === LevelExtension.Subject}
          {targetVerbAndObjects.value[stage].sentence}
        {/if}
      </div>
    </div>
  </div>

  <!-- bottom area -->
  <!-- 1) first show the four pictures as small - one a flows up to be target i middle section -->
  <!-- 2) then show evaluation buttons yes and no -->

  <!-- 1) show small pictures  -->
  {#if currentState === State.WaitToStart || currentState === State.PlayingInstruction || currentState === State.PlayingPromptInitial}
    <div class="bottom-area">
      {#each sendImgs as item (item)}
        <div
          class="send-img"
          animate:flip
          in:receive|global={{ key: item }}
          out:send|global={{ key: item }}
        >
          <picture>
            <source
              type="image/webp"
              srcset={encodeURI(Path.Img + item.img + Extension.Webp)}
              draggable={false}
            />
            <img
              class="small-img"
              style="background-image: url('{encodeURI(
                Path.ImgBg + item.img + Extension.ImgBg,
              )}');
                background-image: image-set('{encodeURI(
                Path.ImgBg + item.img + Extension.WebpBg,
              )}'); 
                background-size: cover;"
              draggable={false}
              src={encodeURI(Path.Img + item.img + Extension.Png)}
              alt=""
            />
          </picture>
        </div>
      {/each}
    </div>
    <!-- 2) show yes and no evaluation buttons -->
  {:else if currentState !== State.GotNoResponse}
    <div class="evaluate-area" class:enhanceEvaluateArea>
      <!-- {#if currentState !== State.RepeatTogether && currentState !== State.RepeatWait} -->
      <div class="response-text">
        <!-- {#if currentState === State.ReceivingAnswer || currentState === State.ReadyForAnswer || currentState === State.HasReceivedAnswer || currentState === State.RepeatWait} -->
        Er du tilfreds?
        <!-- {:else}
              <span class="gray-text"> ... </span>
            {/if} -->
      </div>
      <div class="evaluate-button-area">
        <span class="evaluate-button accept-button">
          <IconButton
            disabled={disableAllButtons || currentState == State.Paused}
            onclick={acceptsOwnAnswer1}
            icon={Icons.ThumbsUp}
            textScaled={true}
          >
            {#snippet message()}Ja{/snippet}
          </IconButton>
        </span>
        <span class="evaluate-button">
          <IconButton
            disabled={disableAllButtons || currentState == State.Paused}
            onclick={rejectsOwnAnswer}
            icon={Icons.ThumbsDown}
            redColor={true}
            textScaled={true}
          >
            {#snippet message()}Nej{/snippet}
          </IconButton>
        </span>
        {#if currentState === State.WaitingForAnswerAfterAnswerNo || currentState === State.WaitForAnswerAfterTryAgain}
          <span class="evaluate-button">
            <IconButton
              disabled={disableAllButtons}
              onclick={repeatSound}
              icon={Icons.Repeat}
              textMediumScaled={true}
              >{#snippet message()}Prøv igen{/snippet}</IconButton
            >
          </span>
          <span class="evaluate-button">
            <IconButton
              disabled={disableAllButtons}
              onclick={givingUp}
              icon={Icons.StopCircleRegular}
              textMediumScaled={true}
              >{#snippet message()}Færdig{/snippet}</IconButton
            >
          </span>
        {/if}
      </div>
    </div>
  {/if}
  <!-- show progress of number of tasks -->
  <div class="progress">
    <ProgressBar
      max={getTotalNumberOfTasks(studentUserData.value.currentLevel)}
      position={progress.value}
    />
  </div>
</main>

<!-- dialog boxes -->

{#if currentState === State.Paused}
  <PauseMessage pauseMessageClicked={pauseClicked} />
{/if}

{#if currentState === State.ExitModalShown}
  <ExitModal yes={exitTask} no={dismissExitModal} />
{/if}

<!-- CSS ===================================================== -->
<style>
  main {
    margin-left: auto;
    margin-right: auto;
    width: 120vh;
    height: calc(var(--vh, 1vh) * 100);
    display: grid;
    grid-template-columns: 96%;
    grid-template-rows: 14% 44% 10% 22% 2%;
    grid-template-areas:
      "top"
      "target"
      "text"
      "imgs"
      "prog";
    gap: 3%;
    padding: 3%;
    padding-top: 2%;
    padding-left: 2%;
    padding-right: 2%;
  }
  @media screen and (max-aspect-ratio: 6/5) {
    main {
      width: 100vw;
    }
  }
  .top-panel {
    grid-area: top;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .target-area {
    grid-area: target;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
  .target {
    height: 100%;
  }
  .progress {
    grid-area: prog;
    width: 100%;
  }
  .image {
    background: white;
    border: solid 1px black;
    width: 100%;
    height: 100%;
  }
  .target-img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: bottom;
  }
  .response-text {
    display: flex;
    justify-content: space-around;
    font-size: 6vmin;
  }
  .evaluate-area {
    grid-area: imgs;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    align-items: space-around;
    border: solid 1px black;
    border-radius: 1vmin;
  }
  .enhanceEvaluateArea {
    border: solid 8px blue;
    background: lightskyblue;
  }
  .evaluate-button-area {
    display: flex;
    justify-content: center;
  }
  .evaluate-button {
    margin-left: 2vmin;
    margin-right: 2vmin;
  }
  .text {
    grid-area: text;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
  .bottom-area {
    grid-area: imgs;
    display: flex;
    justify-content: space-around;
    align-items: center;
  }
  .send-img {
    height: 18vmin;
    width: 23vmin;
    background: white;
  }
  .small-img {
    border: solid 1px black;
    width: 100%;
    height: 90%;
    object-fit: contain;
  }
  .button-text {
    border: solid 1px black;
    background: white;
    width: 80%;
    color: black;
    font-size: 5vmin;
    display: flex;
    align-items: center;
    justify-content: center;
    user-select: none;
    padding-top: 0.1vmin;
    padding-bottom: 0.5vmin;
  }
  .text-start {
    display: flex;
    height: 100%;
    width: 50%;
    align-items: center;
    justify-content: flex-end;
    padding-right: 0.5vmin;
  }
  .short-text-start {
    width: 40%;
  }
  .text-end {
    display: flex;
    height: 100%;
    width: 50%;
    border: none;
    align-items: center;
    padding-bottom: 0;
    justify-content: flex-start;
    border-left: 2px dotted black;
    padding-left: 0.5vmin;
  }
  .long-text-end {
    width: 60%;
  }
  .hidden {
    visibility: hidden;
  }
  .name-caps {
    text-transform: capitalize;
  }
</style>
